/**
 * Mass Role - Cast Pillar
 *
 * Normaliza input sujo para número tipado.
 * Detecta unidades em strings e converte se necessário.
 *
 * @example
 * import { castMass, castMassStrict, detectMassUnit } from '@attrx/role-morphic/mass/cast';
 *
 * castMass('100 kg', 'kilogram');     // 100
 * castMass('100 lb', 'kilogram');     // 45.359237 (converte)
 * castMassStrict('100 kg');           // 100 (não converte)
 * detectMassUnit('100 pounds');       // 'pound'
 */

import { MASS_UNITS, MASS_ALIASES, type MassUnit } from "./constants";
import { convertMass } from "./convert";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

// =============================================================================
// CAST
// =============================================================================

/**
 * Faz cast de input para número, detectando e convertendo unidade se presente
 *
 * @param input - Valor de entrada (number, string, etc)
 * @param targetVariant - Unidade alvo para conversão
 * @returns Número ou null se não puder converter
 *
 * @example
 * castMass(100, 'kilogram');            // 100
 * castMass('100', 'kilogram');          // 100
 * castMass('100 kg', 'kilogram');       // 100
 * castMass('100 lb', 'kilogram');       // 45.359237 (converte)
 * castMass('100 pounds', 'gram');       // 45359.237 (converte)
 * castMass('invalid', 'kilogram');      // null
 */
export function castMass(
  input: unknown,
  targetVariant: MassUnit = "kilogram"
): number | null {
  // Já é número
  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  // String
  if (typeof input === "string") {
    return parseMassString(input, targetVariant);
  }

  return null;
}

/**
 * Faz cast sem conversão de unidade (só parseia o valor literal)
 *
 * @param input - Valor de entrada
 * @returns Número ou null
 *
 * @example
 * castMassStrict('100 kg');   // 100 (ignora unidade, não converte)
 * castMassStrict('100');      // 100
 * castMassStrict(100);        // 100
 */
export function castMassStrict(input: unknown): number | null {
  // Já é número
  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  // String
  if (typeof input === "string") {
    const numValue = parseNumber(input);
    return numValue;
  }

  return null;
}

/**
 * Versão safe de castMass que retorna Result
 */
export function tryCastMass(
  input: unknown,
  targetVariant: MassUnit = "kilogram"
): Result<number> {
  const result = castMass(input, targetVariant);
  if (result === null) {
    return {
      ok: false,
      error: `Cannot cast "${String(input)}" to mass:${targetVariant}`,
    };
  }
  return { ok: true, value: result };
}

// =============================================================================
// DETECTION
// =============================================================================

/**
 * Detecta unidade de massa a partir de uma string
 *
 * @param input - String com possível unidade
 * @returns Unidade detectada ou null
 *
 * @example
 * detectMassUnit('100 kg');        // 'kilogram'
 * detectMassUnit('100 pounds');    // 'pound'
 * detectMassUnit('100 oz');        // 'ounce'
 * detectMassUnit('100');           // null
 */
export function detectMassUnit(input: string): MassUnit | null {
  const trimmed = input.trim().toLowerCase();
  const match = trimmed.match(/^[+-]?[\d.,\s]+\s*(.*)$/);

  if (!match || !match[1]) {
    return null;
  }

  const unitStr = match[1].trim();
  return detectUnit(unitStr);
}

// =============================================================================
// INTERNAL HELPERS
// =============================================================================

/**
 * Parseia string para número, detectando unidade e convertendo se necessário
 */
function parseMassString(input: string, targetVariant: MassUnit): number | null {
  const trimmed = input.trim().toLowerCase();

  // Extrai número e possível unidade
  const match = trimmed.match(/^([+-]?[\d.,\s]+)\s*(.*)$/);
  if (!match) {
    return null;
  }

  const [, numStr, unitStr] = match;

  // Normaliza número
  const numValue = parseNumber(numStr);
  if (numValue === null) {
    return null;
  }

  // Se tem unidade no input, converte se necessário
  const cleanUnitStr = unitStr.trim();
  if (cleanUnitStr) {
    const detectedUnit = detectUnit(cleanUnitStr);
    if (detectedUnit && detectedUnit !== targetVariant) {
      // Converte da unidade detectada para a target
      return convertMass(detectedUnit, targetVariant, numValue);
    }
  }

  return numValue;
}

/**
 * Parseia string numérica considerando formatos brasileiro e americano
 */
function parseNumber(numStr: string): number | null {
  // Remove espaços
  numStr = numStr.replace(/\s/g, "");

  const lastComma = numStr.lastIndexOf(",");
  const lastDot = numStr.lastIndexOf(".");
  const hasComma = lastComma !== -1;
  const hasDot = lastDot !== -1;

  if (hasComma && hasDot) {
    if (lastComma > lastDot) {
      // Brasileiro: 1.000,50
      numStr = numStr.replace(/\./g, "").replace(",", ".");
    } else {
      // Americano: 1,000.50
      numStr = numStr.replace(/,/g, "");
    }
  } else if (hasComma && !hasDot) {
    // Só vírgulas: detecta se é milhar ou decimal
    const afterComma = numStr.split(",").slice(1);
    const isThousandSep = afterComma.every((part) => part.length === 3);
    if (isThousandSep) {
      numStr = numStr.replace(/,/g, "");
    } else {
      numStr = numStr.replace(",", ".");
    }
  } else if (!hasComma && hasDot) {
    // Só pontos: múltiplos pontos = milhar brasileiro
    const afterDot = numStr.split(".").slice(1);
    if (afterDot.length > 1) {
      numStr = numStr.replace(/\./g, "");
    }
  }

  const value = parseFloat(numStr);
  return isNaN(value) ? null : value;
}

/**
 * Detecta unidade a partir de string (símbolo ou alias)
 */
function detectUnit(unitStr: string): MassUnit | null {
  const normalized = unitStr.toLowerCase().trim();

  // Checa aliases primeiro
  if (normalized in MASS_ALIASES) {
    return MASS_ALIASES[normalized] as MassUnit;
  }

  // Checa símbolos e nomes
  for (const [variant, config] of Object.entries(MASS_UNITS)) {
    if (
      config.symbol.toLowerCase() === normalized ||
      config.singular?.toLowerCase() === normalized ||
      config.plural?.toLowerCase() === normalized ||
      variant.toLowerCase() === normalized
    ) {
      return variant as MassUnit;
    }
  }

  return null;
}
